import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Check } from '@huspy/briks-icons';
import {
  Box, Text, Flex, Switch
} from '@huspy/briks-web';
import { VStack } from '@huspy/styled-system/jsx';
import { Image } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';

import { MORTGAGE_USAGE } from '@modules/opportunities/entities/opportunity/const';
import { imagesPath } from '@shared/css.const';
import { MortgageUsage } from '@modules/opportunities/entities/opportunity/opportunity.entity';
import { css } from '@huspy/styled-system/css';

const baseTranslation = 'clients.formSections.mortgageUsageSelectionSlido';

type MortgageUsageCardProps = {
  isActive: boolean;
  checked: boolean;
  onClick: () => void;
  handleIsAssisted: (val: boolean) => void;
  form: UseFormReturnType<any>;
  children: React.ReactNode;
};

const MortgageUsageCard = ({
  isActive,
  onClick,
  handleIsAssisted,
  checked,
  form,
  children,
}: MortgageUsageCardProps) => {
  const { t } = useTranslation();

  const handleSwitchChange = (e: { checked: boolean }) => {
    handleIsAssisted(e.checked);
    form.setFieldValue('is_assisted', e.checked);
  };

  return (
    <VStack
      p='4'
      bg={ isActive ? 'indigo.50' : 'neutral.100' }
      borderRadius='4'
      borderColor={ isActive ? 'indigo.400' : 'base.white' }
      borderWidth='3'
      cursor='pointer'
      onClick={ onClick }
      w='100%'
      data-testid='toggle-assist-btn'
    >
      <Flex w='100%'>
        <Flex
          justify='center'
          align='center'
          mr='auto'
          p='1'
          borderRadius='50%'
          w='5'
          h='5'
          bg={ isActive ? 'black' : 'transparent' }
          borderWidth={ !isActive ? '1' : undefined }
          borderColor={ !isActive ? 'black' : undefined }
        >
          {isActive && <Check color='white' height='20px' width='20px' />}
        </Flex>
        <Switch
          checked={ isActive && checked }
          onCheckedChange={ handleSwitchChange }
          className={ css(
            { '& [data-scope="switch"][data-part="control"]:not([data-state="checked"])': { backgroundColor: 'black.32%' } }
          ) }
        >
          {t(`${baseTranslation}.assisted`)}
        </Switch>
      </Flex>
      {children}
    </VStack>
  );
};

export const MortgageUsageSelection = ({ form }: {
  form: UseFormReturnType<any>;
}) => {
  const { t } = useTranslation();

  const currentUsage = form.getValues().mortgage_usage;
  const initialIsAssisted = form.getValues().is_assisted as boolean;
  const [checked, setChecked] = useState(initialIsAssisted);

  const handleIsAssisted = (val: boolean) => {
    setChecked(val);
  };

  const renderCard = (
    usage: MortgageUsage,
    image: string,
    titleKey: string,
    isSelected: boolean
  ) => (
    <MortgageUsageCard
      isActive={ isSelected }
      onClick={ () => form.setFieldValue('mortgage_usage', usage) }
      form={ form }
      checked={ checked }
      handleIsAssisted={ handleIsAssisted }
    >
      <Box w='80' h='80'>
        <Image src={ `${imagesPath}/${image}` } w='100%' h='100%' />
      </Box>
      <Text textAlign='center' size='lg' fontWeight='semibold'>
        {t(`${baseTranslation}.${titleKey}`)}
      </Text>
      <Text textAlign='center' size='md' color='neutral.800'>
        {checked && isSelected
          ? t(`${baseTranslation}.assistedDesc`)
          : t(`${baseTranslation}.notAssistedDesc`)}
      </Text>
    </MortgageUsageCard>
  );

  return (
    <VStack h='100%' p='8'>
      <Text size='xl' fontWeight='semibold' mb='4'>
        {t(`${baseTranslation}.title`)}
      </Text>
      <Flex direction='column' gap='4'>
        {renderCard(
          MORTGAGE_USAGE.RESIDENTIAL,
          'residential.png',
          'residential',
          currentUsage === MORTGAGE_USAGE.RESIDENTIAL
        )}
        {renderCard(
          MORTGAGE_USAGE.COMMERCIAL,
          'commercial.png',
          'commercial',
          currentUsage === MORTGAGE_USAGE.COMMERCIAL
        )}
      </Flex>
    </VStack>
  );
};
